import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Edit,
    Folder,
    ChevronRight,
    Search,
    X,
    CheckCircle2,
    AlertCircle,
    Package,
    Activity
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const CategoryManager = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        icon: 'Folder'
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await api.get('/categories');
            if (res.data.success) {
                setCategories(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingCategory
                ? `/categories/${editingCategory._id}`
                : '/categories';
            const method = editingCategory ? 'put' : 'post';

            const res = await api[method](url, formData);

            if (res.data.success) {
                toast.success(editingCategory ? 'Category updated' : 'Category created');
                closeModal();
                fetchCategories();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Operation failed');
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            description: category.description || '',
            icon: category.icon || 'Folder'
        });
        setIsAddModalOpen(true);
    };

    const closeModal = () => {
        setIsAddModalOpen(false);
        setEditingCategory(null);
        setFormData({ name: '', description: '', icon: 'Folder' });
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('Are you sure? This will not delete products but remove their category association.')) return;
        try {
            await api.delete(`/categories/${id}`);
            toast.success('Category removed');
            fetchCategories();
        } catch (error) {
            toast.error('Failed to delete category');
        }
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Taxonomy</h1>
                    <p className="text-slate-400 mt-1 text-sm">Organize and manage system categories</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-400 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 w-64 transition-all"
                        />
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-violet-600/20 active:scale-[0.98] flex items-center gap-2"
                    >
                        <Plus size={18} strokeWidth={2.5} /> New Category
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 p-6 rounded-2xl shadow-lg shadow-indigo-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Folder size={80} />
                    </div>
                    <p className="text-indigo-100 text-sm font-medium mb-1">Total Categories</p>
                    <h3 className="text-3xl font-bold text-white">{categories.length}</h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">Active</span>
                        <span className="text-indigo-200 text-xs">System nodes</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 rounded-2xl shadow-lg shadow-emerald-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Activity size={80} />
                    </div>
                    <p className="text-emerald-100 text-sm font-medium mb-1">Active Status</p>
                    <h3 className="text-3xl font-bold text-white">{categories.filter(c => c.status !== 'disabled').length}</h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">98%</span>
                        <span className="text-emerald-100 text-xs">Operational</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-6 rounded-2xl shadow-lg shadow-blue-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Package size={80} />
                    </div>
                    <p className="text-blue-100 text-sm font-medium mb-1">Integrated Items</p>
                    <h3 className="text-3xl font-bold text-white">
                        {categories.reduce((acc, cat) => acc + (cat.products?.length || 0), 0)}
                    </h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">Total</span>
                        <span className="text-blue-100 text-xs">Stock items</span>
                    </div>
                </div>
            </div>

            {/* Categories List View */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-800 bg-slate-900/50">
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Category Name</th>
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 hidden lg:table-cell text-center">Data Usage</th>
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            <AnimatePresence>
                                {filteredCategories.map((category, idx) => (
                                    <motion.tr
                                        key={category._id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="group hover:bg-slate-800/50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400 group-hover:bg-violet-500 group-hover:text-white transition-all shadow-sm">
                                                    <Folder size={18} />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-white group-hover:text-violet-400 transition-colors">{category.name}</h3>
                                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1 max-w-[250px]">{category.description || 'No description provided'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <div className="flex items-center justify-center">
                                                <span className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1 rounded-full text-xs font-medium">
                                                    {category.products?.length || 0} items linked
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(category)}
                                                    className="p-2 text-slate-400 hover:text-white hover:bg-violet-500 rounded-lg transition-all"
                                                    title="Edit Category"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCategory(category._id)}
                                                    className="p-2 text-slate-400 hover:text-white hover:bg-rose-500 rounded-lg transition-all"
                                                    title="Delete Category"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Empty State */}
            {!loading && filteredCategories.length === 0 && (
                <div className="py-20 border-2 border-dashed border-slate-800 rounded-2xl flex flex-col items-center justify-center text-center bg-slate-900/50">
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-6">
                        <Folder className="text-slate-600" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-white">No Categories Found</h3>
                    <p className="text-slate-500 mt-2 text-sm max-w-sm">Use the 'Initialize' button to create your first system category and organize your content.</p>
                </div>
            )}

            {/* Add/Edit Category Modal */}
            <AnimatePresence>
                {isAddModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden relative z-10"
                        >
                            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                                <h2 className="text-xl font-bold text-white">{editingCategory ? 'Update Category' : 'New Category'}</h2>
                                <button onClick={closeModal} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Category Name</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g., Electronics"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Description</label>
                                    <textarea
                                        rows={4}
                                        placeholder="Describe the purpose of this category..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 resize-none transition-all"
                                    />
                                </div>
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-violet-600/20 active:scale-[0.98]"
                                    >
                                        {editingCategory ? 'Save Changes' : 'Create Category'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CategoryManager;
